import { FluidMenuItem, FluidMenuType } from '@lmig/fluid-core';
import { FunctionalComponent, h } from '@stencil/core';

import {
  NavButton,
  NavDivider,
  NavItem,
  NavSubmenu,
  NavSubmenuBtn,
  NavTitle,
} from '../../../common/fluid-navigation.components';
import { FluidFunctionalComponentProps } from '../../../model/functional/fluid-functional-component-props.interface';
import { FluidI18nStore } from '../../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { FluidTreeMenu } from '../fluid-tree-menu';
import { hasSubmenu } from '../utils/fluid-tree-menu.utils';

export interface TreeMenuProps extends FluidFunctionalComponentProps {
  host: FluidTreeMenu;
  menuItem: FluidMenuItem;
  i18nStore?: FluidI18nStore;
}

// ================================================================ //
// -- Public Functional Components

/**
 * Tree Menu Item
 */
export const TreeMenuItem: FunctionalComponent<TreeMenuProps> = (
  props: TreeMenuProps
) => {
  const { menuItem } = props;

  switch (true) {
    case menuItem.divider:
      return <NavDivider class='tree-node tree-menu-divider' />;
    case menuItem.sectionTitle && !!menuItem.label:
      return <NavTitle class='tree-node tree-menu-title' menuItem={menuItem} />;
    case hasSubmenu(menuItem):
      return <TreeMenuSubmenu {...props} />;
    default:
      return <TreeMenuNavButton {...props} />;
  }
};

// ================================================================ //
// -- Private Functional Components

/**
 * Tree Menu Nav Button
 */
const TreeMenuNavButton: FunctionalComponent<TreeMenuProps> = (
  props: TreeMenuProps
) => {
  const { host, menuItem, i18nStore } = props;

  return (
    <NavButton
      class='tree-node tree-menu-link'
      menuItem={menuItem}
      i18nStore={i18nStore}
      menuType={host.menuType}
      theme={host.theme}
      titleAttr={host.displayTitleAttr(menuItem)}
      onClick={(event) => host.handleMenuItemClick(menuItem, event)}
      ref={(el: HTMLElement) => host.setSelectedItemRef(el, menuItem)}
    />
  );
};

/**
 * Tree Menu Submenu Button
 */
const TreeMenuSubmenuButton: FunctionalComponent<TreeMenuProps> = (
  props: TreeMenuProps
) => {
  const { host, menuItem, i18nStore } = props;

  return (
    <NavSubmenuBtn
      menuItem={menuItem}
      i18nStore={i18nStore}
      menuType={host.menuType}
      theme={host.theme}
      titleAttr={host.displayTitleAttr(menuItem)}
      tabIndex={host.submenuButtonInactive(menuItem) && -1}
      onClick={() => host.handleSubmenuButtonClick(menuItem)}
      ref={(el: HTMLElement) => host.setSelectedItemRef(el, menuItem)}
      class={{
        'tree-node': true,
        'tree-menu-submenu-button': true,
        'button-selected':
          menuItem.selected && host.menuType !== FluidMenuType.DROPDOWN,
        'submenu-button-inactive': host.submenuButtonInactive(menuItem),
      }}
    />
  );
};

/**
 * Tree Menu Submenu
 */
const TreeMenuSubmenu: FunctionalComponent<TreeMenuProps> = (
  props: TreeMenuProps
) => {
  const { host, menuItem, i18nStore } = props;

  return [
    submenuHasNavButton(host, menuItem) ? (
      <TreeMenuNavButton {...props} />
    ) : (
      <TreeMenuSubmenuButton {...props} />
    ),
    <div
      class={{
        'submenu-container': true,
        'submenu-expanded': menuItem.submenuOpen,
      }}>
      <NavSubmenu class='tree-menu-submenu' menuItem={menuItem}>
        {menuItem.submenu.map((item: FluidMenuItem) => (
          <NavItem class='tree-menu-nav-item'>
            <TreeMenuItem host={host} menuItem={item} i18nStore={i18nStore} />
          </NavItem>
        ))}
      </NavSubmenu>
    </div>,
  ];
};

// ================================================================ //
// -- Functional Component Utilities (Private)

/**
 * Determines if menu item with submenu should render a "nav button"
 * or "submenu button". Returns true when:
 * - Tree Menu DOES have selectable submenu buttons enabled
 * - Tree Menu does NOT have collapsible submenus
 * - Menu item does NOT have collapsible submenu (i.e. overriding "collapsibleSubmenus")
 */
const submenuHasNavButton = (
  host: FluidTreeMenu,
  menuItem: FluidMenuItem
): boolean => {
  return (
    host.selectableSubmenuButtons &&
    !host.collapsibleSubmenus &&
    !menuItem.submenuCollapsible
  );
};
